import React, { useEffect, useRef, useState } from 'react'
import { FiImage, FiPaperclip, FiVideo, FiX } from 'react-icons/fi'
import UserAvatar from './userAvatar'
import ChatGifPane from './chatGifPane'
import { uploadPostFile } from '../utils/postImages'
import { packCommentMedia } from '../utils/commentMedia'
import '../styles/commentThread.css'

const CommentComposer = ({
  className = 'commentComposer',
  replyTo,
  onCancelReply,
  userPhoto,
  userEmail,
  onFocusAuth,
  onSubmit
}) => {
  const [text, setText] = useState('')
  const [file, setFile] = useState(null)
  const [gifUrl, setGifUrl] = useState('')
  const [gifOpen, setGifOpen] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const photoRef = useRef(null)
  const videoRef = useRef(null)
  const menuRef = useRef(null)
  const preview = gifUrl || (file ? URL.createObjectURL(file) : '')
  const isVideo = Boolean(file && String(file.type || '').startsWith('video'))

  useEffect(() => {
    if (!menuOpen) return undefined
    const close = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) setMenuOpen(false)
    }
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [menuOpen])

  const clearMedia = () => {
    setFile(null)
    setGifUrl('')
  }

  const pickFile = (event) => {
    const next = event.target.files && event.target.files[0]
    event.target.value = ''
    if (!next) return
    setFile(next)
    setGifUrl('')
    setGifOpen(false)
    setMenuOpen(false)
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    if (busy) return
    const nextText = text.trim()
    if (!nextText && !file && !gifUrl) return
    setBusy(true)
    try {
      let media = ''
      if (gifUrl) media = packCommentMedia('gif', gifUrl)
      else if (file) {
        const name = await uploadPostFile(file)
        media = packCommentMedia(isVideo ? 'video' : 'photo', name)
      }
      await onSubmit({ text: nextText, media })
      setText('')
      clearMedia()
      setGifOpen(false)
      setMenuOpen(false)
    } catch (err) {
      window.alert(err?.message || 'Could not send comment')
    } finally {
      setBusy(false)
    }
  }

  return (
    <form className={`${className}${gifOpen ? ' isGifOpen' : ''}`} onSubmit={handleSubmit}>
      {replyTo ? (
        <div className={className.includes('watch') ? 'watchCommentReplyNote' : 'commentReplyNote'}>
          Replying to {replyTo.email}
          <button type="button" onClick={onCancelReply}>Cancel</button>
        </div>
      ) : null}
      <UserAvatar className="commentAvatar" src={userPhoto} name={userEmail} alt="" />
      <div className="commentField" ref={menuRef}>
        <input
          value={text}
          onFocus={onFocusAuth}
          onChange={event => setText(event.target.value)}
          placeholder={replyTo ? `Reply to ${replyTo.email}` : 'Add a comment'}
          maxLength={500}
          disabled={busy}
        />
        <button
          type="button"
          className={`commentClip${menuOpen || gifOpen || preview ? ' isOn' : ''}`}
          onClick={() => {
            setMenuOpen(open => !open)
            setGifOpen(false)
          }}
          aria-label="Attach"
        >
          <FiPaperclip />
        </button>
        {menuOpen ? (
          <div className="commentClipMenu">
            <button type="button" onClick={() => photoRef.current && photoRef.current.click()}>
              <FiImage /> Photo
            </button>
            <button type="button" onClick={() => videoRef.current && videoRef.current.click()}>
              <FiVideo /> Video
            </button>
            <button
              type="button"
              onClick={() => {
                setGifOpen(true)
                setMenuOpen(false)
              }}
            >
              GIF
            </button>
          </div>
        ) : null}
        <input ref={photoRef} type="file" accept="image/*" hidden onChange={pickFile} />
        <input ref={videoRef} type="file" accept="video/*" hidden onChange={pickFile} />
      </div>
      <button type="submit" disabled={busy}>{busy ? '…' : 'Post'}</button>
      {preview ? (
        <div className="commentAttachPreview">
          {isVideo ? <video src={preview} muted playsInline /> : <img src={preview} alt="" />}
          <button type="button" onClick={clearMedia} aria-label="Remove attachment"><FiX /></button>
        </div>
      ) : null}
      <ChatGifPane
        open={gifOpen}
        onPick={(url) => {
          setGifUrl(url)
          setFile(null)
          setGifOpen(false)
          setMenuOpen(false)
        }}
      />
    </form>
  )
}

export default CommentComposer
